import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowUpRight, ArrowRight, Sparkles, CheckCircle2, Layers, Banknote,
  Globe, Radio, Workflow, Cpu, ShieldCheck,
  ChevronDown, ChevronUp, Wallet,
} from "lucide-react";
import LiveReaders from "@/components/marketing/LiveReaders";
import InsidePlatformDiagram from "@/components/marketing/InsidePlatformDiagram";
import InsidePhoneEmailMockups from "@/components/marketing/InsidePhoneEmailMockups";
import InsideTicker from "@/components/marketing/InsideTicker";
import CodeRain from "@/components/marketing/CodeRain";
import { useCodeRain } from "@/lib/siteEffects";
import { LaptopFrame, PhoneFrame } from "@/components/marketing/InsideDeviceFrames";

const ASSETS = "/inside-platform";

// ===== Section 2 — the 4 modes =====
const MODES = [
  {
    Icon: Globe,
    eyebrow: "Mode 01",
    title: "First-party acquisition",
    body: "We operate consumer-facing brands across finance — Assura Cover, QuickFinance, SwiftCash, SolveDebts and more. They drive direct, exclusive intent into the Connexis routing engine.",
    you: "Buyers pay Connexis per accepted lead.",
    tone: "navy",
  },
  {
    Icon: Radio,
    eyebrow: "Mode 02",
    title: "Partner publisher network",
    body: "Approved publishers send qualified enquiries via our API or hosted lander builder. Every accepted lead earns the source 90% of the buyer price.",
    you: "Publishers earn 90%, Connexis keeps a 10% routing margin.",
    tone: "blue",
  },
  {
    Icon: Workflow,
    eyebrow: "Mode 03",
    title: "Exclusive lead distribution",
    body: "Live routing, dedup, postback delivery, AI qualification and a prepaid wallet — buyers receive 100% exclusive enquiries in real time.",
    you: "Wallet debits per accepted lead. No invoices in arrears.",
    tone: "navy",
  },
  {
    Icon: Cpu,
    eyebrow: "Mode 04",
    title: "AI operational systems",
    body: "Voice, chat, WhatsApp and appointment booking — all operated by Connexis, plugged straight into your routing. FCA-aware scripts, sub-second latency, every conversation recorded and auditable.",
    you: "Setup + monthly per system. Custom builds quoted.",
    tone: "blue",
  },
];

// ===== Section 3 — Platform plans reframed =====
const PLANS = [
  {
    slug: "starter",
    name: "Starter",
    monthly: 99,
    desc: "For lean teams launching their first campaigns.",
    included: ["API + dashboard access", "Direct-post & webhooks", "AI lead qualification", "Email + chat support"],
    badge: null,
  },
  {
    slug: "growth",
    name: "Growth",
    monthly: 149,
    desc: "For scaling operators routing multiple sectors.",
    included: ["Everything in Starter", "Ping trees + AI scoring", "Replies inbox + auto-action", "Dedicated success manager"],
    badge: "Most popular",
  },
  {
    slug: "scale",
    name: "Scale",
    monthly: 249,
    desc: "For volume buyers and lead-exchange operators.",
    included: ["Everything in Growth", "White-label portals", "SSO + audit log", "Priority routing rebates"],
    badge: null,
  },
];

// ===== Section 4 — Buyer dashboard tour =====
// Curated: a featured hero (Overview) + 2 sneak-peek surfaces. Less is more —
// gives prospects a clean taste of the buyer experience without overload.
const BUYER_SURFACES = [
  { src: `${ASSETS}/buyer-overview.png`,  mobile: `${ASSETS}/buyer-overview-mobile.png`,
    title: "Overview",             body: "Live KPIs, AI insights, exclusive lead feed and money-in / money-out at a glance — desktop or pocket." },
  { src: `${ASSETS}/buyer-leads.png`,
    title: "Leads feed",           body: "Every accepted enquiry — sector, source, score and disposition — with one-click return + dispute." },
  { src: `${ASSETS}/buyer-replies.png`,
    title: "AI Replies inbox",     body: "Every reply your team receives is auto-classified by intent — hot, reschedule, disqualify, question — then either suggested or actioned for you. Hours of admin gone." },
];

// ===== Section 5 — Publisher portal tour =====
// Same philosophy — one featured hero + 2 sneak-peeks.
const PUB_SURFACES = [
  { src: `${ASSETS}/publisher-overview.png`, mobile: `${ASSETS}/publisher-overview-mobile.png`,
    title: "Publisher overview", body: "Daily accepts, revenue share, conversion and 14-day trend — the only screen most of our top publishers ever load. Built for desktop, runs on the go." },
  { src: `${ASSETS}/publisher-landers.png`,
    title: "Lander builder",     body: "Spin up sector-branded hosted landers in under a minute. A/B variants, source tracking, conversion analytics — no developer needed." },
  { src: `${ASSETS}/publisher-earnings.png`,
    title: "Earnings",           body: "Day-by-day revenue per sector + per accepted lead. Live ledger of every credit and payout." },
];

// ===== Section 6 — Consumer brands =====
const BRANDS = [
  // All four brands shown in laptop frames so the consumer site is fully
  // visible and the brand mark/typography never gets clipped by a phone bezel.
  { name: "Assura Cover",   domain: "assuracover.com",   sector: "Life Insurance",   desktop: `${ASSETS}/assuracover.png` },
  { name: "QuickFinance",   domain: "quickfinance.io",   sector: "Business Finance", desktop: `${ASSETS}/quickfinance.png` },
  { name: "SwiftCash",      domain: "swiftca.sh",        sector: "Short-Term",       desktop: `${ASSETS}/swiftcash.png` },
  { name: "Solve Debts",    domain: "solvedebts.co.uk",  sector: "Debt Help",        desktop: `${ASSETS}/solvedebts.png` },
];

// ===== Section 9 — FAQ =====
const FAQ = [
  { q: "Is the £99 / £149 / £249 plan a lead allowance?",
    a: "No. The plan is platform access — the API, dashboards, routing engine, compliance toolchain and support. Lead spend is separate and pay-as-you-go from your wallet." },
  { q: "Are AI Systems included in any plan?",
    a: "No, AI Systems (Voice, Chat, WhatsApp, Booker, Omnichannel, CRM sync) are separately ordered managed products with their own setup + monthly fees. They plug straight into your routing." },
  { q: "Do you produce your own leads, or only route from partners?",
    a: "Both. We operate first-party consumer brands (see the strip above) that feed direct, exclusive enquiries into the network. They supplement — never displace — partner publisher volume." },
  { q: "What's your margin on every lead?",
    a: "10%, baked into the sector price. If a mortgage lead costs you £38, the publisher receives £34.20 and Connexis keeps £3.80. No hidden uplift, no opaque routing fees." },
  { q: "How is the AI Voice agent built?",
    a: "End-to-end inside Connexis. Sub-300ms latency, FCA-aware compliance scripts, calendar-aware booking and full call recording for audit. You bring the script — we run the agent." },
  { q: "Is there a contract or commitment?",
    a: "Plan is 30-day rolling. Wallet balance never expires. Cancel platform plan, pause campaigns, withdraw your wallet — anytime." },
  { q: "Can I see real dashboard demos before signing up?",
    a: "Yes. The screenshots on this page are pulled live from the running platform. Book a strategy call to drive it yourself with our team." },
];

export default function InsideConnexis() {
  const rain = useCodeRain("inside_connexis");
  return (
    <div className="bg-white">
      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden border-b border-slate-200/80">
        <div className="absolute inset-0 dot-grid dot-grid-fade opacity-80" />
        <div className="absolute top-24 left-1/2 -translate-x-1/2 w-[760px] h-[760px] rounded-full bg-primary/5 blur-3xl" />
        {rain.on && <CodeRain variant="light" intensity={rain.intensity} />}
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-20 lg:pt-28 pb-16 lg:pb-20">
          <Badge variant="outline" className="rounded-full border-slate-300 bg-white/70 backdrop-blur text-slate-700 px-3 py-1" data-testid="inside-badge">
            <span className="size-1.5 rounded-full bg-emerald-500 mr-2 animate-pulse" />
            The Connexis platform, explained
          </Badge>
          <h1 className="mt-6 text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-[-0.04em] text-slate-900 leading-[0.95] max-w-4xl">
            Inside <span className="gradient-text">Connexis.</span>
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl leading-relaxed">
            The full platform, the four modes, the dashboards your team will live in, and exactly what you pay for. One scroll, no surprises.
          </p>
          <div className="mt-8 flex flex-wrap gap-3" data-testid="inside-hero-ctas">
            <Link to="/login"><Button className="rounded-full px-5 h-11 text-[15px]">Sign up now <ArrowUpRight className="size-4" /></Button></Link>
            <Link to="/book-demo"><Button variant="outline" className="rounded-full px-5 h-11 text-[15px] border-slate-300">Talk to engineering <ArrowRight className="size-4" /></Button></Link>
          </div>

          <div className="mt-12 grid sm:grid-cols-4 gap-3 max-w-3xl">
            {[
              ["£99/mo", "Platform access"],
              ["£14–£145", "Per accepted lead"],
              ["10%", "Connexis margin"],
              ["£5,000", "Minimum first top-up"],
            ].map(([v, l], i) => (
              <div key={i} className="rounded-xl border border-slate-200 bg-white/70 backdrop-blur p-4">
                <div className="text-2xl font-semibold tracking-tight text-slate-900 tabular-nums">{v}</div>
                <div className="mt-1 text-[11.5px] uppercase tracking-[0.16em] text-slate-500 font-medium">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SECTION 2 — FOUR MODES ===== */}
      <section className="py-24 sm:py-32 border-b border-slate-200" data-testid="inside-modes">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-primary">The four modes</p>
            <h2 className="mt-3 text-4xl sm:text-5xl tracking-[-0.03em] font-medium text-slate-900">One platform. Four ways money moves.</h2>
            <p className="mt-4 text-slate-600 text-lg">
              Connexis is a four-sided business — we acquire, we route, we distribute, and we automate. Here's exactly how each side works.
            </p>
          </div>

          <div className="mt-12">
            <InsidePlatformDiagram />
          </div>

          <div className="mt-14 grid md:grid-cols-2 gap-5">
            {MODES.map((m, i) => (
              <div key={i} className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-8 hover:shadow-float transition-shadow" data-testid={`inside-mode-${i}`}>
                <div className={`absolute left-0 top-0 bottom-0 w-[3px] ${m.tone === "navy" ? "bg-[#0F172A]" : "bg-[#0055FF]"}`} />
                <div className="flex items-center gap-3">
                  <span className={`inline-grid place-items-center size-10 rounded-lg text-white ${m.tone === "navy" ? "bg-[#0F172A]" : "bg-[#0055FF]"}`}>
                    <m.Icon className="size-5" strokeWidth={1.6} />
                  </span>
                  <span className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-slate-500">{m.eyebrow}</span>
                </div>
                <h3 className="mt-6 text-2xl font-medium tracking-tight text-slate-900">{m.title}</h3>
                <p className="mt-3 text-[14.5px] text-slate-600 leading-relaxed">{m.body}</p>
                <div className="mt-6 pt-5 border-t border-slate-100 flex items-center gap-2 text-[13px] text-slate-700">
                  <Banknote className="size-4 text-[#0055FF] shrink-0" /> {m.you}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SECTION 3 — PLATFORM FEE EXPLAINED ===== */}
      <section className="py-24 sm:py-32 bg-slate-50/60 border-b border-slate-200" data-testid="inside-platform-fee">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-primary">What your fee buys</p>
            <h2 className="mt-3 text-4xl sm:text-5xl tracking-[-0.03em] font-medium text-slate-900">The monthly fee is the operating system.</h2>
            <p className="mt-4 text-slate-600 text-lg">
              Lead spend, AI systems and add-ons are pay-as-you-go on top — metered transparently, never invoiced in arrears.
            </p>
          </div>

          {/* The 3 buckets */}
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            <BucketCard
              Icon={Layers}
              eyebrow="Fixed"
              title="Platform plan"
              cost="From £99/mo"
              body="The rails — API, dashboards, ping-tree engine, AI scoring, compliance toolchain, audit log, support and account management. 30-day rolling, cancel anytime."
            />
            <BucketCard
              Icon={Wallet}
              eyebrow="Pay-as-you-go"
              title="Lead spend"
              cost="From £14 / lead"
              body="Top up your wallet, get debited per accepted lead at one flat sector price. 10% is our margin, 90% goes to the source. Auto-pauses at £0 balance."
            />
            <BucketCard
              Icon={Cpu}
              eyebrow="Optional · metered"
              title="AI + add-ons"
              cost="From £0.05 / event"
              body="AI Systems (setup + monthly), Auto Follow-Up (5p/lead), AI Reply Triage (1p–3p/reply). Order what you need, scale or pause anytime."
            />
          </div>

          {/* The 3 plans */}
          <div className="mt-16">
            <div className="flex items-center gap-2 text-primary mb-4">
              <Sparkles className="size-4" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.18em]">Three plan tiers</span>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              {PLANS.map((p, i) => (
                <div key={p.slug} className={`relative rounded-2xl border bg-white p-7 ${p.badge ? "border-[#0055FF] shadow-float" : "border-slate-200"}`} data-testid={`inside-plan-${p.slug}`}>
                  {p.badge && <span className="absolute top-4 right-4 rounded-full bg-[#0055FF]/10 text-[#0055FF] text-[10px] font-semibold px-2 py-1 tracking-wider uppercase">{p.badge}</span>}
                  <div className="text-sm font-medium text-slate-700">{p.name}</div>
                  <div className="mt-4 flex items-baseline gap-1.5">
                    <span className="text-4xl tracking-tight font-semibold text-slate-900">£{p.monthly}</span>
                    <span className="text-sm text-slate-500">/mo</span>
                  </div>
                  <p className="mt-2 text-sm text-slate-600">{p.desc}</p>
                  <div className="mt-5 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-500">Included</div>
                  <ul className="mt-2 space-y-1.5">
                    {p.included.map((b, k) => <li key={k} className="flex items-start gap-2 text-[13.5px] text-slate-700"><CheckCircle2 className="size-4 text-[#0055FF] shrink-0 mt-0.5" /> {b}</li>)}
                  </ul>
                  <div className="mt-5 pt-4 border-t border-slate-100 text-[12px] text-slate-500 leading-relaxed">
                    Plus: pay-per-lead wallet, optional AI Systems, optional add-ons.
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 text-center text-sm text-slate-500">
              Routing more than 10,000 leads/mo? <Link to="/book-demo" className="text-[#0055FF] hover:underline font-medium">Custom enterprise quote</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION 4 — BUYER DASHBOARD TOUR ===== */}
      <section className="py-24 sm:py-32 border-b border-slate-200" data-testid="inside-buyer-tour">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
            <div className="max-w-2xl">
              <p className="text-xs font-semibold tracking-[0.2em] uppercase text-primary">Inside the buyer portal</p>
              <h2 className="mt-3 text-4xl sm:text-5xl tracking-[-0.03em] font-medium text-slate-900">Live screens, not mockups.</h2>
              <p className="mt-4 text-slate-600 text-lg">Every screenshot is pulled from the running platform. Sign in to see your own version in less than 60 seconds.</p>
            </div>
            <Link to="/login"><Button className="rounded-full px-5 h-11 bg-[#0F172A] hover:bg-[#1e293b]" data-testid="inside-buyer-cta">Open the buyer portal <ArrowUpRight className="size-4" /></Button></Link>
          </div>

          {/* Hero surface — Overview in a laptop frame */}
          <div className="mb-10" data-testid="buyer-surface-0">
            <HeroScreen surface={BUYER_SURFACES[0]} accent="navy" />
          </div>
          {/* Supporting surfaces — just 2, side-by-side, big enough to read */}
          <div className="grid md:grid-cols-2 gap-6">
            {BUYER_SURFACES.slice(1).map((s, i) => (
              <ScreenCard key={s.title} surface={s} testid={`buyer-surface-${i + 1}`} accent="navy" />
            ))}
          </div>
        </div>
      </section>

      {/* ===== SECTION 5 — PUBLISHER PORTAL TOUR ===== */}
      <section className="py-24 sm:py-32 bg-slate-900 text-white border-b border-slate-200" data-testid="inside-publisher-tour">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
            <div className="max-w-2xl">
              <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#3D7BFF]">Inside the publisher portal</p>
              <h2 className="mt-3 text-4xl sm:text-5xl tracking-[-0.03em] font-medium">Built for lead sellers who hate friction.</h2>
              <p className="mt-4 text-slate-300 text-lg">Apply, get approved, go live with one API key. Hosted landers, A/B variants, real-time earnings — never a developer in sight.</p>
            </div>
            <Link to="/publisher/apply"><Button className="rounded-full px-5 h-11 bg-white text-slate-900 hover:bg-slate-100" data-testid="inside-publisher-cta">Apply to sell leads <ArrowUpRight className="size-4" /></Button></Link>
          </div>

          {/* Hero surface — Publisher overview in a laptop frame */}
          <div className="mb-10" data-testid="publisher-surface-0">
            <HeroScreen surface={PUB_SURFACES[0]} accent="dark" />
          </div>
          {/* Supporting surfaces — just 2, full-bleed */}
          <div className="grid md:grid-cols-2 gap-6">
            {PUB_SURFACES.slice(1).map((s, i) => (
              <ScreenCard key={s.title} surface={s} testid={`publisher-surface-${i + 1}`} accent="dark" />
            ))}
          </div>
        </div>
      </section>

      {/* ===== SECTION 6 — CONSUMER BRANDS ===== */}
      <section className="py-24 sm:py-32 border-b border-slate-200" data-testid="inside-brands">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-primary">We don't just route — we produce</p>
            <h2 className="mt-3 text-4xl sm:text-5xl tracking-[-0.03em] font-medium text-slate-900">First-party consumer brands. Direct, exclusive supply.</h2>
            <p className="mt-4 text-slate-600 text-lg">
              Connexis operates these consumer-facing properties across UK finance. They feed direct, exclusive enquiries into the network — supplementing, never displacing, partner publisher volume.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-6 lg:gap-8">
            {BRANDS.map((b) => (
              <div key={b.domain}
                   className="group relative rounded-2xl border border-slate-200 bg-gradient-to-b from-slate-50/40 to-white p-6 sm:p-8 hover:shadow-soft transition-shadow"
                   data-testid={`inside-brand-${b.domain.replace(/\./g, "-")}`}>
                {/* Aurora glow */}
                <div className="absolute inset-x-8 top-6 h-32 rounded-full bg-gradient-to-b from-[#0055FF]/[0.06] to-transparent blur-2xl pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                <div className="relative flex items-end justify-center">
                  <LaptopFrame src={b.desktop} alt={`${b.name} consumer site`} testid={`brand-laptop-${b.domain.replace(/\./g, "-")}`} />
                </div>
                {/* Caption */}
                <div className="mt-6 pt-5 border-t border-slate-100 flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="font-semibold text-slate-900 text-[15px]">{b.name}</div>
                    <div className="mt-0.5 text-[11.5px] uppercase tracking-[0.14em] text-slate-500 font-medium">{b.sector}</div>
                  </div>
                  <div className="inline-flex items-center gap-1.5 text-[12px] font-mono text-slate-500">
                    <span className="size-1.5 rounded-full bg-emerald-500" /> {b.domain}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 rounded-2xl border border-slate-200 bg-slate-50/60 p-6 flex items-start gap-4">
            <ShieldCheck className="size-5 text-[#0055FF] shrink-0 mt-0.5" />
            <p className="text-[14px] text-slate-700 leading-relaxed">
              <span className="font-semibold text-slate-900">A note for partner publishers.</span> Connexis-operated brands feed the same routing engine as your traffic — they don't compete for the same buyer slots. Partner volume remains the majority of routed enquiries across every sector.
            </p>
          </div>
        </div>
      </section>

      {/* ===== SECTION 7 — CONSUMER EXPERIENCE: iPhone SMS + EMAIL ===== */}
      <section className="py-24 sm:py-32 bg-slate-50/60 border-b border-slate-200" data-testid="inside-consumer-exp">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-primary">From applicant to qualified lead</p>
            <h2 className="mt-3 text-4xl sm:text-5xl tracking-[-0.03em] font-medium text-slate-900">How a real consumer becomes your lead.</h2>
            <p className="mt-4 text-slate-600 text-lg">
              When someone applies on a Connexis brand, our AI adviser qualifies them by SMS in seconds and confirms by email. Here&apos;s exactly what they see — branded, compliant, and warmed up before your team ever picks up the phone.
            </p>
          </div>

          <div className="mt-14">
            <InsidePhoneEmailMockups />
          </div>
        </div>
      </section>

      {/* ===== SECTION 8 — TICKER ===== */}
      <InsideTicker />

      {/* ===== SECTION 9 — FAQ ===== */}
      <section className="py-24 sm:py-32 bg-white border-t border-slate-200" data-testid="inside-faq">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-primary">Frequently asked</p>
            <h2 className="mt-3 text-4xl sm:text-5xl tracking-[-0.03em] font-medium text-slate-900">The seven questions everyone asks.</h2>
            <p className="mt-4 text-slate-600 text-lg">Plain answers. No marketing fog.</p>
          </div>

          <div className="mt-12 space-y-3">
            {FAQ.map((item, i) => (
              <FaqRow key={i} item={item} testid={`inside-faq-${i}`} defaultOpen={i === 0} />
            ))}
          </div>
        </div>
      </section>

      {/* ===== SECTION 10 — FINAL CTA ===== */}
      <section className="pb-24 pt-12" data-testid="inside-final-cta">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl bg-slate-900 text-white p-12 sm:p-16">
            <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.5) 1px, transparent 0)", backgroundSize: "22px 22px" }} />
            <div className="relative grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-4xl sm:text-5xl tracking-[-0.03em] font-medium">Ready to operate like infrastructure?</h3>
                <p className="mt-4 text-slate-300 max-w-md">Sign up and you're routing in minutes. Or get our team to walk you through your acquisition stack first — both paths take you live this week.</p>
              </div>
              <div className="flex md:justify-end gap-3 flex-wrap">
                <Link to="/login"><Button data-testid="inside-cta-signup" className="rounded-full px-5 h-11 bg-white text-slate-900 hover:bg-slate-100">Sign up now <ArrowUpRight className="size-4" /></Button></Link>
                <Link to="/book-demo"><Button variant="outline" className="rounded-full px-5 h-11 border-white/30 text-white hover:bg-white/10" data-testid="inside-cta-talk">Talk to engineering <ArrowRight className="size-4" /></Button></Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <LiveReaders page="inside-connexis" />
    </div>
  );
}

// ===== Helper components =====
function BucketCard({ Icon, eyebrow, title, cost, body }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-7">
      <span className="inline-grid place-items-center size-11 rounded-lg bg-[#0F172A] text-white">
        <Icon className="size-5" strokeWidth={1.6} />
      </span>
      <div className="mt-5 text-[10.5px] font-semibold uppercase tracking-[0.16em] text-slate-500">{eyebrow}</div>
      <div className="mt-1 text-[18px] font-semibold text-slate-900 tracking-tight">{title}</div>
      <div className="mt-3 text-[13px] text-[#0055FF] font-mono tabular-nums font-semibold">{cost}</div>
      <p className="mt-3 text-[13.5px] text-slate-600 leading-relaxed">{body}</p>
    </div>
  );
}

function ScreenCard({ surface, testid, accent }) {
  const isDark = accent === "dark";
  return (
    <div
      className={`group relative rounded-2xl overflow-hidden border transition-all duration-500 ${
        isDark
          ? "border-white/10 bg-white/[0.04] hover:bg-white/[0.06] hover:border-white/20"
          : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-float"
      }`}
      data-testid={testid}
    >
      {/* Aurora glow */}
      <div className={`absolute inset-x-4 -top-6 h-24 rounded-full blur-2xl pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-700 ${isDark ? "bg-[#3D7BFF]/15" : "bg-[#0055FF]/10"}`} />
      {/* Window chrome — macOS-style traffic lights */}
      <div className={`relative flex items-center gap-1.5 px-4 py-2.5 border-b ${isDark ? "bg-slate-800/60 border-white/5" : "bg-slate-50 border-slate-100"}`}>
        <span className="size-2.5 rounded-full bg-rose-400/80" />
        <span className="size-2.5 rounded-full bg-amber-400/80" />
        <span className="size-2.5 rounded-full bg-emerald-400/80" />
        <div className={`ml-3 flex-1 max-w-[170px] h-4 rounded-md text-[9.5px] font-mono px-2 flex items-center ${isDark ? "bg-white/[0.06] text-white/40" : "bg-white text-slate-400 border border-slate-200"}`}>
          <span className="truncate">connex.is/{surface.title.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}</span>
        </div>
      </div>
      {/* Image */}
      <div className={`relative aspect-[16/10] overflow-hidden ${isDark ? "bg-slate-900" : "bg-slate-100"}`}>
        <img
          src={surface.src}
          alt={`${surface.title} screenshot`}
          loading="lazy"
          className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.02]"
          onError={(e) => {
            e.target.style.display = "none";
            const parent = e.target.parentElement;
            parent.classList.add(isDark ? "bg-slate-800" : "bg-gradient-to-br", isDark ? "" : "from-slate-100", isDark ? "" : "to-slate-200");
          }}
        />
      </div>
      {/* Caption */}
      <div className="relative p-6">
        <h3 className={`text-[16px] font-semibold tracking-tight ${isDark ? "text-white" : "text-slate-900"}`}>{surface.title}</h3>
        <p className={`mt-2 text-[13.5px] leading-relaxed ${isDark ? "text-slate-300" : "text-slate-600"}`}>{surface.body}</p>
      </div>
    </div>
  );
}

function HeroScreen({ surface, accent }) {
  const isDark = accent === "dark";
  const hasMobile = !!surface.mobile;
  return (
    <div className="relative">
      {/* Aurora wash behind the device cluster */}
      <div className={`absolute inset-x-0 top-1/3 h-2/3 rounded-full blur-3xl pointer-events-none opacity-60 ${isDark ? "bg-[#3D7BFF]/15" : "bg-[#0055FF]/10"}`} />
      <div className={`relative grid items-center gap-8 lg:gap-10 ${hasMobile ? "lg:grid-cols-[1.55fr_auto_1fr]" : "lg:grid-cols-[1.45fr_1fr]"}`}>
        {/* Laptop column */}
        <div className="order-2 lg:order-1">
          <LaptopFrame src={surface.src} alt={`${surface.title} — Connexis dashboard`} testid={`hero-${surface.title.toLowerCase().replace(/\s+/g, "-")}`} />
        </div>
        {/* Dedicated phone column — no overlap with laptop or text */}
        {hasMobile && (
          <div className="order-3 lg:order-2 mx-auto lg:mx-0 w-[140px] sm:w-[160px] lg:w-[130px] xl:w-[150px]">
            <PhoneFrame src={surface.mobile} alt={`${surface.title} — Connexis mobile view`} testid={`hero-mobile-${surface.title.toLowerCase().replace(/\s+/g, "-")}`} />
          </div>
        )}
        {/* Text column */}
        <div className={`order-1 ${hasMobile ? "lg:order-3" : "lg:order-2"}`}>
          <div className={`inline-flex items-center gap-2 text-[10.5px] font-semibold uppercase tracking-[0.18em] px-2.5 py-1 rounded-full ${isDark ? "bg-[#3D7BFF]/15 text-[#3D7BFF]" : "bg-[#0055FF]/10 text-[#0055FF]"}`}>
            <span className={`size-1.5 rounded-full ${isDark ? "bg-[#3D7BFF]" : "bg-[#0055FF]"}`} />
            Featured surface
          </div>
          <h3 className={`mt-4 text-3xl sm:text-4xl tracking-[-0.02em] font-medium ${isDark ? "text-white" : "text-slate-900"}`}>{surface.title}</h3>
          <p className={`mt-4 text-[15px] leading-relaxed ${isDark ? "text-slate-300" : "text-slate-600"}`}>{surface.body}</p>
          {hasMobile && (
            <div className={`mt-5 inline-flex items-center gap-2 text-[12px] font-medium ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              <span className={`inline-flex items-center gap-1.5 ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                <svg viewBox="0 0 24 24" className="size-3.5" fill="none" stroke="currentColor" strokeWidth="2"><rect x="5" y="2" width="14" height="20" rx="3" /><path d="M11 19h2" strokeLinecap="round" /></svg>
                Mobile-first
              </span>
              · runs flawlessly on a phone too
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FaqRow({ item, testid, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div className={`rounded-xl border bg-white overflow-hidden transition-colors ${open ? "border-slate-300 shadow-soft" : "border-slate-200"}`} data-testid={testid}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full px-5 sm:px-6 py-5 flex items-center justify-between gap-4 text-left transition-colors hover:bg-slate-50/60 focus-visible:bg-slate-50/60 focus:outline-none"
        aria-expanded={open}
      >
        <span className={`text-[15px] font-medium ${open ? "text-slate-900" : "text-slate-900"}`}>{item.q}</span>
        {open ? <ChevronUp className="size-4 text-slate-500 shrink-0" /> : <ChevronDown className="size-4 text-slate-500 shrink-0" />}
      </button>
      {open && (
        <div className="px-5 sm:px-6 pt-1 pb-6 border-t border-slate-100">
          <p className="mt-4 text-[14px] text-slate-600 leading-relaxed">{item.a}</p>
        </div>
      )}
    </div>
  );
}
